import { useSessionStore } from "../../../Stores/Session";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

interface RequestConfig {
  method?: HttpMethod;
  url: string;
  data?: any;
  headers?: Record<string, string | boolean>;
  params?: Record<string, any>;
  _retryCount?: number;
}

interface HttpResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
}

interface RetryConfig {
  retries: number;
  retryDelay: number;
  retryCondition?: (error: any) => boolean;
}

const defaultRetryConfig: RetryConfig = {
  retries: 3,
  retryDelay: 3000,
  retryCondition: (error: any) => {
    // Retry on network errors, timeouts, and 5xx server errors
    if (!error.status) {
      // Network error or timeout
      return true;
    }

    const status = error.status;
    // >= 500 for internal server errors
    // 408 for timeout
    // 429 for too many requests
    return status >= 500 || status === 408 || status === 429;
  },
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export class HttpClientXHR {
  public static baseURL: string;
  private defaultHeaders: Record<string, string | boolean>;

  constructor() {
    const { apiUrl } = useSessionStore.getState();
    HttpClientXHR.baseURL = apiUrl || "https://dashapi.gleap.io";
    this.defaultHeaders = {
      Accept: "application/json",
      "Content-Type": "application/json; charset=utf-8",
      "Access-Control-Allow-Credentials": true,
    };
  }

  public static setBaseURL(url: string) {
    HttpClientXHR.baseURL = url;
  }

  private buildURL(url: string, params?: Record<string, any>): string {
    const fullUrl = `${HttpClientXHR.baseURL}${url}`;
    if (params) {
      const query = Object.entries(params)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join("&");
      return fullUrl + (fullUrl.includes("?") ? "&" : "?") + query;
    }
    return fullUrl;
  }

  // Manually inject tokens and other custom headers into every request.
  private injectToken(
    headers: Record<string, string | boolean>
  ): Record<string, string | boolean> {
    const { session, sdkKey } = useSessionStore.getState();

    if (sdkKey) {
      headers["api-token"] = sdkKey;
    }

    if (session && session.gleapId) {
      headers["gleap-id"] = session.gleapId;
    }

    if (session && session.gleapHash) {
      headers["gleap-hash"] = session.gleapHash;
    }

    return headers;
  }

  private async makeRequest<T = any>(
    config: RequestConfig
  ): Promise<HttpResponse<T>> {
    return new Promise<HttpResponse<T>>((resolve, reject) => {
      const { method = "GET", url, data, headers, params } = config;
      const xhr = new XMLHttpRequest();
      const fullUrl = this.buildURL(url, params);

      xhr.open(method, fullUrl, true);

      // Merge default headers and custom headers then inject token info
      let allHeaders: Record<string, string | boolean> = {
        ...this.defaultHeaders,
        ...headers,
      };
      allHeaders = this.injectToken(allHeaders);

      Object.keys(allHeaders).forEach((key) => {
        xhr.setRequestHeader(key, String(allHeaders[key]));
      });

      xhr.onreadystatechange = () => {
        if (xhr.readyState === XMLHttpRequest.DONE) {
          let responseData;
          try {
            responseData = JSON.parse(xhr.responseText);
          } catch (e) {
            responseData = xhr.responseText;
          }

          // Parse response headers
          const responseHeaders: Record<string, string> = {};
          const headerString = xhr.getAllResponseHeaders();
          if (headerString) {
            headerString.split("\r\n").forEach((line) => {
              const parts = line.split(": ");
              if (parts.length === 2) {
                responseHeaders[parts[0].toLowerCase()] = parts[1];
              }
            });
          }

          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({
              data: responseData,
              status: xhr.status,
              statusText: xhr.statusText,
              headers: responseHeaders,
            });
          } else {
            reject({
              status: xhr.status,
              statusText: xhr.statusText,
              data: responseData,
              headers: responseHeaders,
            });
          }
        }
      };

      xhr.onerror = () => {
        reject({
          status: 0,
          statusText: "Network Error",
          data: null,
          headers: {},
        });
      };

      if (data) {
        xhr.send(JSON.stringify(data));
      } else {
        xhr.send();
      }
    });
  }

  async request<T = any>(config: RequestConfig): Promise<HttpResponse<T>> {
    const retryCount = config._retryCount || 0;

    try {
      return await this.makeRequest<T>(config);
    } catch (error) {
      // Check if we should retry
      if (retryCount >= defaultRetryConfig.retries) {
        throw error;
      }

      if (
        !defaultRetryConfig.retryCondition ||
        !defaultRetryConfig.retryCondition(error)
      ) {
        throw error;
      }

      // Increment retry count
      const retryConfig = { ...config, _retryCount: retryCount + 1 };

      // Exponential backoff with base 3: delay triples with each retry
      const delay = defaultRetryConfig.retryDelay * Math.pow(3, retryCount);
      await sleep(delay);

      // Retry the request
      return this.request<T>(retryConfig);
    }
  }

  get<T = any>(
    url: string,
    config?: Omit<RequestConfig, "method" | "url">
  ): Promise<HttpResponse<T>> {
    return this.request<T>({ ...config, method: "GET", url });
  }

  post<T = any>(
    url: string,
    data?: any,
    config?: Omit<RequestConfig, "method" | "url" | "data">
  ): Promise<HttpResponse<T>> {
    return this.request<T>({ ...config, method: "POST", url, data });
  }

  put<T = any>(
    url: string,
    data?: any,
    config?: Omit<RequestConfig, "method" | "url" | "data">
  ): Promise<HttpResponse<T>> {
    return this.request<T>({ ...config, method: "PUT", url, data });
  }

  delete<T = any>(
    url: string,
    config?: Omit<RequestConfig, "method" | "url">
  ): Promise<HttpResponse<T>> {
    return this.request<T>({ ...config, method: "DELETE", url });
  }
}
